// StreetSheetListPage.js — Admin view for all street sheet submissions
// Filterable by date range + crew lead, expandable rows, status management
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, ClipboardList, Search, ChevronDown, ChevronUp,
  MapPin, Users, Calendar, CheckCircle, Loader, Image, Navigation,
  RefreshCw, Filter
} from 'lucide-react';
import { colors, STREET_SHEET_CONFIG } from '../config/constants';

const StreetSheetListPage = ({ darkMode, setCurrentPage, loggedInUser }) => {
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [message, setMessage] = useState(null);

  // Filters
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [filterSince, setFilterSince] = useState(weekAgo);
  const [filterUntil, setFilterUntil] = useState(today);
  const [filterCrewLead, setFilterCrewLead] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchSheets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSince) params.append('since', filterSince);
      if (filterUntil) params.append('until', filterUntil);
      if (filterCrewLead.trim()) params.append('crew_lead', filterCrewLead.trim());
      if (filterStatus) params.append('status', filterStatus);

      const resp = await fetch(`/api/street-sheets?${params}`);
      const data = await resp.json();
      if (data.success) {
        setSheets(data.data || []);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load street sheets' });
      setTimeout(() => setMessage(null), 3000);
    }
    setLoading(false);
  };

  useEffect(() => { fetchSheets(); }, []);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const resp = await fetch('/api/street-sheets', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          status: newStatus,
          reviewed_by: loggedInUser?.name || 'Admin',
          reviewed_at: new Date().toISOString(),
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setSheets(prev => prev.map(s => s.id === id ? { ...s, status: newStatus, reviewed_by: loggedInUser?.name || 'Admin', reviewed_at: new Date().toISOString() } : s));
        setMessage({ type: 'success', text: `Sheet marked as ${newStatus}` });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update status' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const parsePhotos = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  };

  const parseStreets = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  };

  const inputStyle = {
    padding: '8px 12px',
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    backgroundColor: darkMode ? '#1a2332' : '#ffffff',
    color: textColor,
    fontSize: '0.85rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const statusColor = (s) => {
    if (s === 'Reviewed') return accentSecondary;
    if (s === 'Submitted') return accentPrimary;
    return mutedColor;
  };

  // Summary counts
  const submitted = sheets.filter(s => s.status === 'Submitted').length;
  const reviewed = sheets.filter(s => s.status === 'Reviewed').length;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '24px 16px' }}>
      {/* Toast */}
      {message && (
        <div style={{
          position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', backgroundColor: message.type === 'success' ? accentSecondary : '#ef4444',
          color: '#fff', borderRadius: '10px', zIndex: 1000, fontSize: '0.9rem', fontWeight: '500',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <button onClick={() => setCurrentPage('admin-dashboard')} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            backgroundColor: 'transparent', border: `1px solid ${borderColor}`,
            borderRadius: '8px', color: textColor, cursor: 'pointer', fontSize: '0.9rem',
          }}>
            <ArrowLeft size={18} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: textColor, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ClipboardList size={24} style={{ color: accentPrimary }} />
              Street Sheets
            </h1>
            <p style={{ color: mutedColor, margin: '4px 0 0', fontSize: '0.85rem' }}>
              All crew location submissions
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            backgroundColor: cardBg, borderRadius: '10px', padding: '16px',
            border: `1px solid ${borderColor}`, textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: accentPrimary }}>{sheets.length}</div>
            <div style={{ fontSize: '0.8rem', color: mutedColor }}>Total</div>
          </div>
          <div style={{
            backgroundColor: cardBg, borderRadius: '10px', padding: '16px',
            border: `1px solid ${borderColor}`, textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: accentPrimary }}>{submitted}</div>
            <div style={{ fontSize: '0.8rem', color: mutedColor }}>Submitted</div>
          </div>
          <div style={{
            backgroundColor: cardBg, borderRadius: '10px', padding: '16px',
            border: `1px solid ${borderColor}`, textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: accentSecondary }}>{reviewed}</div>
            <div style={{ fontSize: '0.8rem', color: mutedColor }}>Reviewed</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', padding: '16px', marginBottom: '20px',
          border: `1px solid ${borderColor}`, display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end',
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: mutedColor, fontSize: '0.75rem' }}>From</label>
            <input type="date" value={filterSince} onChange={(e) => setFilterSince(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: mutedColor, fontSize: '0.75rem' }}>To</label>
            <input type="date" value={filterUntil} onChange={(e) => setFilterUntil(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: mutedColor, fontSize: '0.75rem' }}>Crew Lead</label>
            <input type="text" value={filterCrewLead} onChange={(e) => setFilterCrewLead(e.target.value)} placeholder="Filter by name..." style={{ ...inputStyle, width: '160px' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: mutedColor, fontSize: '0.75rem' }}>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
              <option value="">All</option>
              <option value="Submitted">Submitted</option>
              <option value="Reviewed">Reviewed</option>
            </select>
          </div>
          <button onClick={fetchSheets} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', backgroundColor: accentPrimary, color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem',
          }}>
            <Search size={14} /> Search
          </button>
        </div>

        {/* Sheet List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: mutedColor }}>
            <Loader size={32} className="spin" style={{ marginBottom: '12px' }} />
            <p>Loading street sheets...</p>
          </div>
        ) : sheets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: mutedColor }}>
            <ClipboardList size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p>No street sheets found for this date range</p>
          </div>
        ) : (
          sheets.map((sheet) => {
            const streets = parseStreets(sheet.streets);
            const photos = parsePhotos(sheet.photos);
            const isExpanded = expandedId === sheet.id;

            return (
              <div key={sheet.id} style={{
                backgroundColor: cardBg, borderRadius: '12px', marginBottom: '12px',
                border: `1px solid ${borderColor}`, overflow: 'hidden',
              }}>
                {/* Row header */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : sheet.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 20px', cursor: 'pointer',
                    borderBottom: isExpanded ? `1px solid ${borderColor}` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Calendar size={14} style={{ color: mutedColor, flexShrink: 0 }} />
                        <span style={{ fontWeight: '600', color: textColor, fontSize: '0.95rem' }}>
                          {formatDate(sheet.sheet_date)}
                        </span>
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600',
                          backgroundColor: statusColor(sheet.status) + '20', color: statusColor(sheet.status),
                        }}>
                          {sheet.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: mutedColor }}>
                        {sheet.company_name} — {sheet.crew_number || 'Crew 1'} {sheet.crew_lead ? `(${sheet.crew_lead})` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {streets.length > 0 && (
                      <span style={{ fontSize: '0.75rem', color: mutedColor }}>
                        {streets.length} street{streets.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {photos.length > 0 && (
                      <span style={{ fontSize: '0.75rem', color: accentPrimary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Image size={12} /> {photos.length}
                      </span>
                    )}
                    {isExpanded ? <ChevronUp size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '2px' }}>Contact</div>
                        <div style={{ fontSize: '0.9rem', color: textColor }}>{sheet.contact_name} {sheet.contact_phone ? `(${sheet.contact_phone})` : ''}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '2px' }}>Crew Lead</div>
                        <div style={{ fontSize: '0.9rem', color: textColor }}>{sheet.crew_lead || 'N/A'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '2px' }}>Crew Size</div>
                        <div style={{ fontSize: '0.9rem', color: textColor }}>{sheet.crew_size || 'N/A'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '2px' }}>Work Type</div>
                        <div style={{ fontSize: '0.9rem', color: textColor }}>{sheet.work_type || 'N/A'}</div>
                      </div>
                    </div>

                    {sheet.ended_yesterday && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '2px' }}>Ended Yesterday</div>
                        <div style={{ fontSize: '0.9rem', color: textColor }}>{sheet.ended_yesterday}</div>
                      </div>
                    )}

                    {/* Streets */}
                    {streets.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '8px' }}>Streets</div>
                        {streets.map((s, i) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 12px', backgroundColor: darkMode ? '#1a2332' : '#f1f5f9',
                            borderRadius: '6px', marginBottom: '4px',
                          }}>
                            <MapPin size={14} style={{ color: accentPrimary, flexShrink: 0 }} />
                            <span style={{ fontSize: '0.9rem', color: textColor }}>
                              {s.name}{s.crossStreet ? ` @ ${s.crossStreet}` : ''}{s.notes ? ` — ${s.notes}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {sheet.notes && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '2px' }}>Notes</div>
                        <div style={{ fontSize: '0.9rem', color: textColor }}>{sheet.notes}</div>
                      </div>
                    )}

                    {/* GPS */}
                    {sheet.gps_lat && (
                      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Navigation size={14} style={{ color: accentPrimary }} />
                        <span style={{ fontSize: '0.85rem', color: mutedColor }}>
                          GPS: {Number(sheet.gps_lat).toFixed(5)}, {Number(sheet.gps_lng).toFixed(5)}
                        </span>
                      </div>
                    )}

                    {/* Photos */}
                    {photos.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '8px' }}>Photos</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {photos.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt="" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${borderColor}` }} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Submitted by + review action */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', paddingTop: '12px', borderTop: `1px solid ${borderColor}` }}>
                      <div style={{ fontSize: '0.8rem', color: mutedColor }}>
                        Submitted by {sheet.submitted_by || 'Unknown'} {sheet.submitted_by_email ? `(${sheet.submitted_by_email})` : ''}
                        {sheet.reviewed_by && (
                          <span> &bull; Reviewed by {sheet.reviewed_by}</span>
                        )}
                      </div>
                      {sheet.status === 'Submitted' && (
                        <button
                          onClick={() => handleStatusUpdate(sheet.id, 'Reviewed')}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '6px 16px', backgroundColor: accentSecondary, color: '#fff',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem',
                          }}
                        >
                          <CheckCircle size={14} /> Mark Reviewed
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default StreetSheetListPage;
